// frontend/src/utils/urlUtils.js
/**
 * URL validation and sanitization utilities
 * 
 * Security-focused utilities for validating notification action URLs
 * to prevent XSS attacks via javascript:, data:, or external URLs.
 */

/**
 * Validates a notification action URL for safe navigation
 * 
 * Rules:
 * - Rejects dangerous protocols: javascript:, data:, vbscript:, etc.
 * - Only allows relative paths or same-origin URLs
 * - Sanitizes and validates before navigation
 * 
 * @param {string} url - The URL to validate
 * @returns {Object} - { isValid: boolean, sanitizedUrl: string | null, error: string | null }
 */
export const validateNotificationUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return {
      isValid: false,
      sanitizedUrl: null,
      error: 'URL is required and must be a string'
    };
  }

  // Trim whitespace
  const trimmedUrl = url.trim();
  
  if (!trimmedUrl) {
    return {
      isValid: false,
      sanitizedUrl: null,
      error: 'URL cannot be empty'
    };
  }

  // Reject dangerous protocols
  const dangerousProtocols = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'about:',
    'chrome:',
    'chrome-extension:',
  ];

  const lowerUrl = trimmedUrl.toLowerCase();
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return {
        isValid: false,
        sanitizedUrl: null,
        error: `Dangerous protocol detected: ${protocol}`
      };
    }
  }

  // Check if it's a relative path (starts with /)
  if (trimmedUrl.startsWith('/')) {
    // Relative path - safe to use
    // Remove any query params that might contain malicious content
    try {
      const urlObj = new URL(trimmedUrl, window.location.origin);
      return {
        isValid: true,
        sanitizedUrl: urlObj.pathname + (urlObj.search || ''),
        error: null
      };
    } catch (e) {
      // If URL parsing fails, check if it's a simple relative path
      if (/^\/[^\/].*/.test(trimmedUrl)) {
        return {
          isValid: true,
          sanitizedUrl: trimmedUrl.split('?')[0], // Remove query params for safety
          error: null
        };
      }
      return {
        isValid: false,
        sanitizedUrl: null,
        error: 'Invalid URL format'
      };
    }
  }

  // Check if it's a same-origin absolute URL
  try {
    const urlObj = new URL(trimmedUrl, window.location.origin);
    const currentOrigin = window.location.origin;
    
    if (urlObj.origin === currentOrigin) {
      // Same origin - safe to use
      return {
        isValid: true,
        sanitizedUrl: urlObj.pathname + (urlObj.search || ''),
        error: null
      };
    } else {
      // Different origin - reject for security
      return {
        isValid: false,
        sanitizedUrl: null,
        error: 'External URLs are not allowed for security reasons'
      };
    }
  } catch (e) {
    // URL parsing failed - might be malformed
    return {
      isValid: false,
      sanitizedUrl: null,
      error: 'Invalid URL format'
    };
  }
};

const LEGACY_URL_REWRITES = {
  '/admin/leave-approvals': '/admin/leave?tab=applications',
};

function applyLegacyRewrites(url) {
  if (!url) return url;
  const basePath = url.split('?')[0];
  // Old trade-transfer notifications pointed ?tt= at the root path instead of
  // the Trade Transfer page that actually handles the param.
  if (basePath === '/' && url.includes('tt=')) {
    return '/self-service/trade-transfer?' + url.split('?')[1];
  }
  const replacement = LEGACY_URL_REWRITES[basePath];
  if (!replacement) return url;
  const existingQuery = url.includes('?') ? '&' + url.split('?')[1] : '';
  const hasQuery = replacement.includes('?');
  return hasQuery ? replacement + existingQuery : replacement + (existingQuery ? '?' + existingQuery.slice(1) : '');
}

/**
 * Safely navigate to a notification action URL
 * Uses React Router's navigate function after validation
 * 
 * @param {string} url - The URL to navigate to
 * @param {Function} navigate - React Router's navigate function
 * @returns {boolean} - true if navigation was successful, false otherwise
 */
export const safeNavigateToNotificationUrl = (url, navigate) => {
  const rewrittenUrl = applyLegacyRewrites(url);
  const validation = validateNotificationUrl(rewrittenUrl);
  
  if (!validation.isValid) {
    console.warn('Invalid notification URL:', validation.error, url);
    return false;
  }

  try {
    navigate(validation.sanitizedUrl);
    return true;
  } catch (error) {
    console.error('Navigation error:', error);
    return false;
  }
};

export default {
  validateNotificationUrl,
  safeNavigateToNotificationUrl,
};

